import { SessionProvider } from 'next-auth/react';
import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { ExperimentProvider } from 'src/services/experiments/ContextProviders/ExperimentProvider';
import { GraphqlProvider } from 'src/services/graphql/ContextProviders/GraphqlProvider';
import { FunctionComponentWithChildren } from 'src/utils/types';
import { useMigrateToZillowState } from 'src/experiences/MigrateToZillow/hooks/useMigrateToZillowState';
import { WiredZillowNavbar } from 'src/components/ZillowNavbar';
import { HEADER_VERSIONS } from 'src/components/ZillowNavbar/constants';
import { globalErrorHandler } from 'src/common/globalErrorHandler';
import { DevEnvironmentWarning } from 'src/components/DevEnvironmentWarning';
import { QueryParamsManager } from 'src/services/QueryParameterManager';
import { getPublicSettingsValues } from 'src/services/settings/getPublicSettingsValues';
import { getMarkdownCMSDocument } from 'src/services/max/getMarkdownCMSDocument';
import * as ApplicationConfigurationService from 'src/services/config/ApplicationConfigurationService.client';
import * as RuntimeConfigurationService from 'src/services/config/domain/RuntimeConfigurationService/RuntimeConfigurationService.client';
import { ClientSideConfigInjector } from 'src/services/config/components/ClientSideConfigInjector.wired';
import * as RouteConfigurationService from 'src/services/config/domain/RouteConfigurationService/RouteConfigurationService.client';
import { UniversalServerSideProps } from 'src/utils/getters/getUniversalPageProps/utils/getAllUniversalProps';
import { DatadogProvider } from '../services/datadog/ContextProviders/DatadogProvider';
import { UnauthorizedAccessGuardProvider } from './UnauthorizedAccessGuardProvider';
import { AnalyticsProvider } from './AnalyticsProvider';
import { DestructiveOperationActionHandlerProvider } from './DestructiveOperationActionHandlerProvider/DestructiveOperationActionHandlerProvider';

const ZillowZHLFooter = dynamic(
    async () => {
        const { ZillowZHLFooter: Component } = await import(
            /* webpackChunkName: "Footer" */
            '@zillow/zhl-page-structure'
        );
        return Component;
    },
    // This footer causes SSR hydration issues
    { ssr: false }
);

/**
 * This is really the only type that makes sense here
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PageSpecificPropsShape = Record<string, any>;

export const UniversalWrappers: FunctionComponentWithChildren<UniversalServerSideProps & PageSpecificPropsShape> = ({
    session,
    children,
    clientSideConfig,
    ...rest
}) => {
    useMigrateToZillowState();
    useEffect(() => {
        window.addEventListener('error', globalErrorHandler);
        return () => {
            window.removeEventListener('error', globalErrorHandler);
        };
    });
    const configService = ApplicationConfigurationService.ConfigProvider.getInstance();
    const shouldShowDevWarning =
        configService.runtimeConfig.deploymentEnvironment ===
        RuntimeConfigurationService.Domain.DeploymentEnvironment.DEVELOPMENT;
    return (
        <>
            <ClientSideConfigInjector clientSideConfig={clientSideConfig} />
            <GraphqlProvider>
                <SessionProvider
                    basePath={`${RouteConfigurationService.Domain.ApplicationRoute.API_BASE}/auth`}
                    refetchOnWindowFocus
                    session={session}>
                    <ExperimentProvider {...rest}>
                        <AnalyticsProvider>
                            <DestructiveOperationActionHandlerProvider>
                                <WiredZillowNavbar version={HEADER_VERSIONS.ZHL_DEFAULT} />
                                <UnauthorizedAccessGuardProvider>
                                    {shouldShowDevWarning && <DevEnvironmentWarning />}
                                    <DatadogProvider loanID={rest.loanID} trackingID={rest.trackingID}>
                                        {children}
                                    </DatadogProvider>
                                </UnauthorizedAccessGuardProvider>
                                <ZillowZHLFooter
                                    queryParameters={QueryParamsManager.store}
                                    onGeneralCallZHL={() => {}} //what event to send for this?
                                    onError={() => {}}
                                    getPublicSettingsValues={getPublicSettingsValues}
                                    getMarkdownCMSDocument={getMarkdownCMSDocument}
                                />
                            </DestructiveOperationActionHandlerProvider>
                        </AnalyticsProvider>
                    </ExperimentProvider>
                </SessionProvider>
            </GraphqlProvider>
        </>
    );
};
